import gsap from "gsap";
import barba from "@barba/core";
import {
  Map,
  VectorLayer,
  Polygon,
  Label,
  Marker,
  TileLayer,
  ui,
} from "maptalks-gl";
import { Loader } from "./loader";
import { select, selectAll, capitalizeFirst } from "./_utils";
import openModal from "./helpers/openModal";
import closeModal from "./helpers/closeModal";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";

gsap.registerPlugin(ScrollTrigger, Observer);

// --- VARIABILI GLOBALI (Stato) ---
let map = null;
let loaderInstance = new Loader();
let isFirstLoad = true;

// Stato Memoria Mappa
let savedMapState = null;

// Stato Modale e Navigazione
let lastModalArgs = null;
let isModalOpen = false;
let restoreModal = false; // NUOVO: Flag per sapere se riaprire la modale al ritorno
let autoOpenGallery = false; // Flag per aprire la gallery automaticamente sulla pagina prodotto

const originalOpenModal = openModal;
const originalCloseModal = closeModal;

// --- FUNZIONI DI SUPPORTO UI ---
const openModalTracked = (elName, options) => {
  lastModalArgs = { elName, options };
  isModalOpen = true;
  const arrowBtn = document.querySelector(".arrow__btn");
  if (arrowBtn) arrowBtn.classList.add("is-open");

  originalOpenModal(elName, options);
};

const closeModalTracked = () => {
  isModalOpen = false;
  const arrowBtn = document.querySelector(".arrow__btn");
  if (arrowBtn) arrowBtn.classList.remove("is-open");
  originalCloseModal();
};

const initUIEvents = () => {
  const controlsDialog = select(".controls__dialog");
  const controlsSpan = select("#controls");
  const controlsCloseBtn = select(".controls__dialog .close");
  const logo = select(".logo");
  const arrowBtn = select(".overlay__arrow");

  const onClickControls = () => {
    if (!controlsDialog) return;
    controlsDialog.style.display = "flex";
    gsap.to(controlsDialog, { duration: 0.3, opacity: 1 });
  };

  const onClickCloseControls = () => {
    if (!controlsDialog) return;
    gsap.to(controlsDialog, {
      duration: 0.3,
      opacity: 0,
      onComplete: () => {
        controlsDialog.style.display = "none";
      },
    });
  };

  if (controlsCloseBtn) controlsCloseBtn.onclick = onClickCloseControls;
  if (controlsSpan) controlsSpan.onclick = onClickControls;

  if (arrowBtn) {
    arrowBtn.onclick = () => {
      const modalElement = document.querySelector(".overlay__modal");
      const isVisible =
        modalElement &&
        (modalElement.style.display === "block" ||
          modalElement.style.transform === "translateX(0)");

      if (isVisible && isModalOpen) {
        closeModalTracked();
      } else if (lastModalArgs) {
        openModalTracked(lastModalArgs.elName, lastModalArgs.options);
      }
    };
  }

  const backBtn = document.querySelector(".back");
  if (backBtn && backBtn.tagName !== "A") {
    backBtn.onclick = (e) => {
      e.preventDefault();
      barba.go("index.html");
    };
  }

  if (logo) {
    logo.onclick = (e) => {
      if (map) {
        e.preventDefault();
        map.animateTo(
          { center: [0, 0], zoom: 3 },
          { duration: 800, easing: "out" },
        );
      }
    };
  }
};

// --- LOGICA MAPPA (HOME) ---

function destroyMap() {
  if (map) {
    savedMapState = {
      center: map.getCenter(),
      zoom: map.getZoom(),
      pitch: map.getPitch(),
      bearing: map.getBearing(),
    };
    map.remove();
    map = null;
  }
}

const initHomeMap = () => {
  if (map) return;
  const mapContainer = document.getElementById("map");
  if (!mapContainer) return;

  // Fade iniziale
  gsap.set(mapContainer, { opacity: 0 });
  mapContainer.innerHTML = "";

  const viewConfig = savedMapState
    ? savedMapState
    : { center: [0, 0], zoom: 3 };

  map = new Map("map", {
    control: true,
    center: viewConfig.center,
    zoom: viewConfig.zoom,
    pitch: viewConfig.pitch || 0,
    bearing: viewConfig.bearing || 0,
    minZoom: 0,
    maxZoom: 10,
    attribution: false,
    baseLayer: new TileLayer("base", { renderer: "gl", opacity: 0 }),
  });

  setTimeout(() => {
    if (map) {
      map.checkSize();
      map.setView(viewConfig);
      // Fade in mappa
      gsap.to(mapContainer, {
        opacity: 1,
        duration: 1.2,
        ease: "power2.out",
        clearProps: "opacity",
      });
    }
  }, 200);

  const regionLayer = new VectorLayer("regionLayer").addTo(map);
  const cityLayer = new VectorLayer("cityLayer", { visible: false }).addTo(map);
  const comuneLayer = new VectorLayer("comuneLayer", { visible: false }).addTo(
    map,
  );

  const regions = [
    {
      id: 1,
      name: "Contacts",
      center: [-54, 28],
      section: "contacts",
      geometry: new Polygon([
        [
          [-58, 32],
          [-54, 34],
          [-50, 32],
          [-48, 28],
          [-49, 24],
          [-52, 20],
          [-56, 18],
          [-58, 22],
          [-58, 28],
          [-58, 32],
        ],
      ]),
      cities: [
        {
          id: "city1a",
          name: "Porto Sereno",
          coordinates: [-55.2, 31.2],
          geometry: new Polygon([
            [
              [-57.0, 31.0],
              [-56.0, 30.5],
              [-54.0, 30.2],
              [-52.5, 31.0],
              [-52.2, 32.5],
              [-53.0, 33.0],
              [-55.0, 32.8],
              [-56.5, 32.5],
              [-57.0, 31.0],
            ],
          ]),
        },
        {
          id: "city1b",
          name: "Valle Solare",
          coordinates: [-50.8, 26.8],
          geometry: new Polygon([
            [
              [-51.8, 25.5],
              [-50.5, 25.2],
              [-50.0, 26.5],
              [-49.8, 28.0],
              [-50.5, 28.5],
              [-51.2, 28.2],
              [-52.0, 27.5],
              [-51.8, 25.5],
            ],
          ]),
        },
        {
          id: "city1c",
          name: "Monte Nebbioso",
          coordinates: [-55.4, 22.1],
          geometry: new Polygon([
            [
              [-58.0, 23.0],
              [-57.0, 24.5],
              [-55.0, 24.8],
              [-53.0, 23.5],
              [-52.5, 22.0],
              [-53.2, 21.5],
              [-55.0, 21.0],
              [-56.5, 20.5],
              [-57.5, 21.5],
              [-58.0, 23.0],
            ],
          ]),
        },
        {
          id: "city1d",
          name: "Fiume Calmo",
          coordinates: [-52.3, 29.5],
          geometry: new Polygon([
            [
              [-53.0, 29.5],
              [-52.3, 30.2],
              [-50.5, 29.8],
              [-50.8, 28.5],
              [-51.5, 27.8],
              [-53.0, 29.5],
            ],
          ]),
        },
      ],
    },
    {
      id: 2,
      name: "About",
      center: [-37.3, 52.3],
      section: "about",
      geometry: new Polygon([
        [
          [-42, 54],
          [-39, 57],
          [-35, 56],
          [-33, 53],
          [-34, 50],
          [-37, 48],
          [-40, 49],
          [-42, 52],
          [-42, 54],
        ],
      ]),
      cities: [
        {
          id: "city2a",
          name: "concept",
          coordinates: [-38.2, 56.1],
          // Teardrop / leaf shape
          geometry: new Polygon([
            [
              [-38.2, 56.6],
              [-37.9, 56.5],
              [-37.6, 56.2],
              [-37.5, 55.9],
              [-37.6, 55.6],
              [-37.9, 55.4],
              [-38.2, 55.3],
              [-38.5, 55.4],
              [-38.8, 55.5],
              [-39.0, 55.8],
              [-38.9, 56.1],
              [-38.6, 56.4],
              [-38.2, 56.6],
            ],
          ]),
        },
        {
          id: "city2b",
          name: "filosofy",
          coordinates: [-36.2, 53.6],
          // Elongated horizontal crescent
          geometry: new Polygon([
            [
              [-36.8, 53.6],
              [-36.5, 53.9],
              [-36.1, 54.0],
              [-35.7, 53.9],
              [-35.4, 53.7],
              [-35.3, 53.4],
              [-35.5, 53.2],
              [-35.8, 53.1],
              [-36.2, 53.0],
              [-36.5, 53.1],
              [-36.8, 53.3],
              [-36.8, 53.6],
            ],
          ]),
        },
        {
          id: "city2c",
          name: "quality",
          coordinates: [-37.8, 50.6],
          // Compact star-like blob
          geometry: new Polygon([
            [
              [-37.8, 51.1],
              [-37.5, 51.0],
              [-37.3, 50.7],
              [-37.2, 50.4],
              [-37.4, 50.1],
              [-37.7, 49.9],
              [-38.0, 50.0],
              [-38.3, 50.2],
              [-38.4, 50.5],
              [-38.3, 50.8],
              [-38.1, 51.0],
              [-37.8, 51.1],
            ],
          ]),
        },
        {
          id: "city2d",
          name: "paolo",
          coordinates: [-40.7, 51.6],
          // Vertical bean shape
          geometry: new Polygon([
            [
              [-40.7, 52.2],
              [-40.4, 52.1],
              [-40.2, 51.8],
              [-40.1, 51.5],
              [-40.2, 51.2],
              [-40.3, 50.9],
              [-40.6, 50.7],
              [-40.9, 50.8],
              [-41.1, 51.0],
              [-41.2, 51.3],
              [-41.1, 51.7],
              [-41.0, 52.0],
              [-40.7, 52.2],
            ],
          ]),
        },
      ],
    },
    {
      id: 3,
      name: "Guitars",
      center: [30, -40],
      section: "guitars",
      geometry: new Polygon([
        [
          [22, -33],
          [28, -30],
          [35, -32],
          [40, -37],
          [42, -43],
          [39, -48],
          [33, -50],
          [27, -48],
          [23, -43],
          [22, -38],
          [22, -33],
        ],
      ]),
      cities: [
        {
          id: "city3a",
          name: "boom",
          coordinates: [26, -38],
          geometry: new Polygon([
            [
              [25.0, -36.6],
              [25.8, -36.4],
              [26.6, -36.5],
              [27.4, -36.9],
              [27.8, -37.4],
              [27.6, -38.0],
              [27.8, -38.6],
              [27.2, -39.2],
              [26.4, -39.4],
              [25.2, -39.2],
              [24.6, -38.6],
              [24.4, -37.8],
              [24.5, -37.2],
              [25.0, -36.6],
            ],
          ]),
        },
        {
          id: "city3b",
          name: "roar",
          coordinates: [35, -42],
          geometry: new Polygon([
            [
              [33.6, -41.4],
              [34.0, -40.8],
              [34.8, -40.2],
              [35.6, -40.0],
              [36.4, -40.2],
              [37.2, -40.6],
              [37.6, -41.2],
              [37.4, -41.8],
              [36.8, -42.0],
              [37.0, -42.6],
              [36.6, -43.2],
              [35.8, -43.0],
              [35.2, -43.4],
              [34.6, -43.2],
              [34.0, -42.8],
              [33.6, -42.6],
              [33.2, -42.0],
              [33.6, -41.4],
            ],
          ]),
        },
        {
          id: "city3c",
          name: "slurp",
          coordinates: [31, -36],
          geometry: new Polygon([
            [
              [30.0, -34.2],
              [30.8, -33.6],
              [31.6, -33.8],
              [32.4, -34.0],
              [33.2, -34.6],
              [33.6, -35.4],
              [33.4, -36.2],
              [33.0, -36.8],
              [32.6, -37.4],
              [31.8, -37.8],
              [31.0, -38.0],
              [30.2, -37.6],
              [29.4, -37.0],
              [28.8, -36.4],
              [28.6, -35.6],
              [29.0, -34.8],
              [29.6, -34.4],
              [30.0, -34.2],
            ],
          ]),
        },
        {
          id: "city3d",
          name: "wow",
          coordinates: [29, -44],
          geometry: new Polygon([
            [
              [28.4, -42.4],
              [29.2, -42.2],
              [30.0, -42.6],
              [30.6, -43.2],
              [30.8, -43.8],
              [30.4, -44.6],
              [30.6, -45.2],
              [30.0, -45.8],
              [29.2, -45.6],
              [28.4, -45.8],
              [27.6, -45.2],
              [27.2, -44.6],
              [27.0, -43.8],
              [27.4, -43.2],
              [27.8, -42.8],
              [28.4, -42.4],
            ],
          ]),
        },
      ],
    },
  ];

  const comuniData = {
    city1a: [
      {
        name: "Email",
        coordinates: [-55.6, 30.1],
        tooltip: "info@tarazum.com",
        href: "mailto:info@tarazum.com",
        // geometry: new Polygon([
        //   [
        //     [-55.4, 30.3],
        //     [-55.7, 30.4],
        //     [-56.0, 30.2],
        //     [-56.0, 29.9],
        //     [-55.7, 29.8],
        //     [-55.4, 29.9],
        //     [-55.3, 30.1],
        //     [-55.4, 30.3],
        //   ],
        // ]),
      },
      {
        name: "Phone",
        coordinates: [-52.8, 32.4],
        tooltip: "+39 338 685 5006",
        href: "tel:+393386855006",
        // geometry: new Polygon([
        //   [
        //     [-52.6, 32.7],
        //     [-52.9, 32.8],
        //     [-53.1, 32.6],
        //     [-53.0, 32.2],
        //     [-52.7, 32.1],
        //     [-52.5, 32.3],
        //     [-52.6, 32.7],
        //   ],
        // ]),
      },
    ],

    city1b: [
      {
        name: "Where",
        coordinates: [-51.2, 26.4],
        tooltip: "Via Ponchielli, Meda (MB), Italy",
        href: "#",
        target: "_blank",
        // geometry: new Polygon([
        //   [
        //     [-51.8, 26.6],
        //     [-51.2, 26.7],
        //     [-50.7, 26.6],
        //     [-50.6, 26.3],
        //     [-50.8, 26.1],
        //     [-51.3, 26.1],
        //     [-51.8, 26.3],
        //     [-51.8, 26.6],
        //   ],
        // ]),
      },
    ],

    city1c: [
      {
        name: "Facebook",
        coordinates: [-57, 24],
        tooltip: "Seguici su Facebook",
        href: "https://www.facebook.com/tarazumguitars",
        target: "_blank",
        // geometry: new Polygon([
        //   [
        //     [-56.8, 24.3],
        //     [-57.1, 24.3],
        //     [-57.4, 24.1],
        //     [-57.3, 23.8],
        //     [-57.0, 23.7],
        //     [-56.7, 23.9],
        //     [-56.7, 24.1],
        //     [-56.8, 24.3],
        //   ],
        // ]),
      },
      {
        name: "Instagram",
        coordinates: [-55.8, 21.6],
        tooltip: "Seguici su Instagram",
        href: "https://www.instagram.com/tarazumguitars",
        target: "_blank",
        // geometry: new Polygon([
        //   [
        //     [-55.6, 21.9],
        //     [-55.9, 21.9],
        //     [-56.1, 21.7],
        //     [-56.0, 21.4],
        //     [-55.7, 21.3],
        //     [-55.5, 21.5],
        //     [-55.5, 21.7],
        //     [-55.6, 21.9],
        //   ],
        // ]),
      },
      {
        name: "YouTube",
        coordinates: [-53.0, 22.6],
        tooltip: "Guarda i video su YouTube",
        href: "https://www.youtube.com/@tarazumguitars4421",
        target: "_blank",
        // geometry: new Polygon([
        //   [
        //     [-53.5, 22.8],
        //     [-53.0, 22.9],
        //     [-52.6, 22.7],
        //     [-52.6, 22.4],
        //     [-52.9, 22.2],
        //     [-53.4, 22.3],
        //     [-53.6, 22.5],
        //     [-53.5, 22.8],
        //   ],
        // ]),
      },
    ],

    city3a: [
      {
        name: "diapason",
        coordinates: [26.5, -37.2],
        geometry: new Polygon([
          [
            [26.6, -37.0],
            [26.8, -37.2],
            [26.7, -37.5],
            [26.4, -37.6],
            [26.2, -37.4],
            [26.2, -37.1],
            [26.5, -37.0],
            [26.6, -37.0],
          ],
        ]),
      },
      {
        name: "monoblocco",
        coordinates: [25.5, -38.5],
        geometry: new Polygon([
          [
            [25.2, -38.3],
            [25.6, -38.2],
            [25.9, -38.4],
            [25.9, -38.7],
            [25.5, -38.9],
            [25.1, -38.7],
            [25.1, -38.5],
            [25.2, -38.3],
          ],
        ]),
      },
    ],

    city3b: [
      {
        name: "spring",
        coordinates: [35.8, -41.1],
        geometry: new Polygon([
          [
            [35.5, -40.8],
            [35.9, -40.7],
            [36.2, -41.0],
            [36.2, -41.4],
            [35.8, -41.5],
            [35.4, -41.3],
            [35.4, -41.0],
            [35.5, -40.8],
          ],
        ]),
      },
      {
        name: "tretrecinque",
        coordinates: [34.5, -42.5],
        geometry: new Polygon([
          [
            [34.0, -42.2],
            [34.5, -42.1],
            [35.0, -42.3],
            [35.0, -42.7],
            [34.6, -42.9],
            [34.1, -42.8],
            [33.9, -42.5],
            [34.0, -42.2],
          ],
        ]),
      },
      {
        name: "venere",
        coordinates: [36.5, -41.8],
        geometry: new Polygon([
          [
            [36.2, -41.5],
            [36.6, -41.4],
            [36.9, -41.6],
            [36.9, -42.0],
            [36.6, -42.2],
            [36.2, -42.0],
            [36.1, -41.7],
            [36.2, -41.5],
          ],
        ]),
      },
    ],

    city3c: [
      {
        name: "felipe",
        coordinates: [31.6, -34.8],
        geometry: new Polygon([
          [
            [31.3, -34.5],
            [31.7, -34.4],
            [32.0, -34.6],
            [32.1, -35.0],
            [31.8, -35.2],
            [31.4, -35.1],
            [31.2, -34.8],
            [31.3, -34.5],
          ],
        ]),
      },
      {
        name: "mono",
        coordinates: [30.2, -36.5],
        geometry: new Polygon([
          [
            [30.3, -36.2],
            [30.6, -36.3],
            [30.7, -36.6],
            [30.5, -36.8],
            [30.1, -36.9],
            [29.9, -36.6],
            [30.0, -36.3],
            [30.3, -36.2],
          ],
        ]),
      },
      {
        name: "pinderbass",
        coordinates: [32.5, -34.8],
        geometry: new Polygon([
          [
            [32.3, -34.4],
            [32.6, -34.4],
            [32.8, -34.7],
            [32.7, -35.1],
            [32.5, -35.3],
            [32.2, -35.2],
            [32.1, -34.9],
            [32.2, -34.6],
            [32.3, -34.4],
          ],
        ]),
      },
    ],

    city3d: [
      {
        name: "klein",
        coordinates: [29.5, -43.2],
        geometry: new Polygon([
          [
            [29.2, -42.9],
            [29.6, -42.8],
            [29.9, -43.0],
            [30.0, -43.3],
            [29.7, -43.6],
            [29.3, -43.5],
            [29.1, -43.3],
            [29.2, -42.9],
          ],
        ]),
      },
    ],
  };

  const backgroundPolygon = new Polygon(
    [
      [
        [-90, 45],
        [-80, 65],
        [-55, 75],
        [-20, 78],
        [15, 70],
        [45, 55],
        [75, 35],
        [90, 0],
        [80, -40],
        [55, -70],
        [20, -80],
        [-20, -78],
        [-55, -70],
        [-80, -40],
        [-90, -5],
        [-90, 20],
        [-90, 45],
      ],
    ],
    {
      symbol: {
        polygonFill: "#eeece2",
        polygonOpacity: 1,
        lineWidth: 10,
        lineColor: "#f7f5ee",
        lineOpacity: 1,
      },
      cursor: "auto",
    },
  );
  regionLayer.addGeometry(backgroundPolygon);

  let activeRegionId = null;

  function getRegionAtMapCenter() {
    const center = map.getCenter();
    for (const region of regions) {
      if (region.geometry.containsPoint(center)) return region.id;
    }
    return null;
  }

  function createComuneTooltip(comune) {
    if (!comune.tooltip) return null;
    let tooltipContent = comune.tooltip;
    if (comune.href)
      tooltipContent = `<a href="${comune.href}" class="tooltip-link" ${comune.target ? `target="${comune.target}"` : ""}>${comune.tooltip}</a>`;
    return new ui.ToolTip(tooltipContent, {
      showTimeout: 200,
      animation: "fade",
      animationDuration: 200,
      animationOnHide: true,
      dx: 0,
      dy: 5,
      cssName: "custom-tooltip",
    });
  }

  function addComuniForCity(cityId) {
    const comuni = comuniData[cityId];
    for (const comune of comuni || []) {
      const handleComuneClick = () => {
        if (comune.href) {
          if (comune.target === "_blank")
            window.open(comune.href, "_blank", "noopener");
          else window.location.href = comune.href;
          return;
        }
        openModalTracked(comune.name, {
          section: "guitars",
          typology: "guitar",
        });
        map.animateTo(
          { center: comune.coordinates, zoom: 10 },
          { duration: 800, easing: "out" },
        );
      };

      if (comune.geometry) {
        comune.geometry.setSymbol({
          polygonFill: "#d1cfc1",
          polygonOpacity: 1,
          lineWidth: 10,
          lineColor: "#d1cfc1",
          lineOpacity: 0.5,
        });
        comune.geometry.setOptions({ cursor: "pointer" });
        comune.geometry.on("click", handleComuneClick);
        comuneLayer.addGeometry(comune.geometry);
      }

      const comuneMarker = new Marker(comune.coordinates, {
        symbol: {
          markerWidth: 6,
          markerHeight: 6,
          markerType: "ellipse",
          markerFill: "#333842",
          markerLineWidth: 0,
        },
      });
      const comuneNameLabel = new Label(
        capitalizeFirst(comune.name),
        comune.coordinates,
        {
          textSymbol: {
            textFaceName: "inter",
            // textFaceName: "arimo",
            textSize: 14,
            textFill: "#333842",
            textDy: 12,
          },
          cursor: "pointer",
          boxStyle: {
            padding: [15, 15],
            symbol: {
              markerFill: "#d8d6cb",
              markerLineWidth: 0,
              markerFillOpacity: 0.01,
            },
          },
        },
      );
      const tooltip = createComuneTooltip(comune);
      if (tooltip) tooltip.addTo(comuneNameLabel);

      comuneMarker.on("click", handleComuneClick);
      comuneNameLabel.on("click", handleComuneClick);
      comuneLayer.addGeometry(comuneMarker);
      comuneLayer.addGeometry(comuneNameLabel);
    }
  }

  function addComuniForRegion(region) {
    if (!region) return;
    for (const city of region.cities || []) addComuniForCity(city.id);
  }

  function addCitiesForRegion(regionId) {
    const region = regions.find((r) => r.id === regionId);
    if (!region) return;
    for (const city of region.cities || []) {
      const cityNameLabel = new Label(
        city.name.toUpperCase(),
        city.coordinates,
        {
          textSymbol: {
            textFaceName: "inter",
            textSize: 16,
            textFill: "#636363",
            textDy: 0,
            textWeight: "bold",
          },
          cursor: "pointer",
          boxStyle: {
            padding: [15, 15],
            symbol: {
              markerFill: "#d8d6cb",
              markerLineWidth: 0,
              markerFillOpacity: 0.01,
            },
          },
        },
      );
      cityNameLabel.on("click", () => {
        activeRegionId = regionId;
        openModalTracked(city.name, {
          section: region.section,
          typology: region.section === "guitars" ? "series" : null,
        });
        map.animateTo(
          { center: city.coordinates, zoom: 9 },
          { duration: 1000, easing: "out" },
        );
      });
      // Setup city polygon styling and add to layer
      if (city.geometry) {
        city.geometry.setSymbol({
          polygonFill: "#d8d6c9",
          polygonOpacity: 1,
          lineWidth: 10,
          lineColor: "#d8d6c9",
          lineOpacity: 0.5,
        });
        city.geometry.setOptions({ cursor: "pointer" });
        city.geometry.on("click", () => {
          activeRegionId = regionId;
          openModalTracked(city.name, {
            section: region.section,
            typology: region.section === "guitars" ? "series" : null,
          });
          map.animateTo(
            { center: city.coordinates, zoom: 9 },
            { duration: 1000, easing: "out" },
          );
        });
        cityLayer.addGeometry(city.geometry);
      }

      cityLayer.addGeometry(cityNameLabel);
    }
  }

  function updateLayerVisibility() {
    const currentZoom = map.getZoom();
    cityLayer.clear();
    comuneLayer.clear();
    cityLayer.hide();
    comuneLayer.hide();
    const regionAtCenter = getRegionAtMapCenter();
    if (regionAtCenter && activeRegionId !== regionAtCenter)
      activeRegionId = regionAtCenter;
    else if (!regionAtCenter && activeRegionId) activeRegionId = null;

    if (activeRegionId) {
      const region = regions.find((r) => r.id === activeRegionId);
      if (region) {
        const isContactsRegion = region.section === "contacts";
        if (currentZoom >= 5 && currentZoom < 8) {
          if (isContactsRegion) {
            addComuniForRegion(region);
            comuneLayer.show();
          } else {
            addCitiesForRegion(region.id);
            cityLayer.show();
          }
        } else if (currentZoom >= 8) {
          if (!isContactsRegion) {
            addCitiesForRegion(region.id);
            cityLayer.show();
          }
          addComuniForRegion(region);
          comuneLayer.show();
        }
      }
    }
  }

  regions.forEach((region) => {
    region.geometry.setId(region.id);
    region.geometry.setSymbol({
      polygonFill: "#e4e2d7",
      lineWidth: 5,
      lineColor: "#e4e2d7",
    });
    region.geometry._setOptions({ cursor: "default" });
    const regionNameLabel = new Label(region.name, region.center, {
      textSymbol: {
        textFaceName: "inter",
        textSize: 28,
        textFill: "#636363",
        textHaloFill: "#333",
        textHaloRadius: 0,
        textWeight: "200",
      },
      cursor: "pointer",
    });
    const handleRegionClick = () => {
      activeRegionId = region.id;

      // 1. Prima chiudiamo la modale (animazione slide out)
      if (isModalOpen) closeModalTracked();

      // 2. Resettiamo la memoria e la freccia
      lastModalArgs = null;
      const arrowBtn = document.querySelector(".arrow__btn");
      if (arrowBtn) arrowBtn.classList.remove("is-open");

      // 3. Nascondiamo tutto completamente dopo un breve delay per mostrare il movimento
      gsap.to(".overlay__modal", {
        delay: 0.4, // Aspetta l'inizio del close per dare fluidità
        opacity: 0,
        duration: 0.5,
        onComplete: () => {
          const modalEl = document.querySelector(".overlay__modal");
          if (modalEl) modalEl.style.display = "none";
        },
      });

      map.animateTo(
        { center: region.center, zoom: 6 },
        { duration: 1000, easing: "out" },
      );
    };
    regionNameLabel.on("click", handleRegionClick);
    region.geometry.on("click", handleRegionClick);
    regionLayer.addGeometry(region.geometry);
    regionLayer.addGeometry(regionNameLabel);
  });

  map.on("zoomend", updateLayerVisibility);
  map.on("moveend", updateLayerVisibility);
  updateLayerVisibility();

  // Listen for guitar-select events from swiper slides in series modal
  document.addEventListener("guitar-select", (e) => {
    const guitarName = e.detail?.guitarName;
    if (!guitarName || !map) return;

    // Find the guitar's coordinates in comuniData
    let guitarCoordinates = null;
    for (const cityId of Object.keys(comuniData)) {
      const comuni = comuniData[cityId];
      const found = comuni.find(
        (c) => c.name.toLowerCase() === guitarName.toLowerCase(),
      );
      if (found) {
        guitarCoordinates = found.coordinates;
        break;
      }
    }

    // Center the map on the guitar
    if (guitarCoordinates) {
      map.animateTo(
        { center: guitarCoordinates, zoom: 10 },
        { duration: 800, easing: "out" },
      );
    }
  });
};

// --- LOGICA PAGINA PRODOTTO ---

function initProductPage() {
  const overlay = select(".overlay");
  const elImage = select(".element__image__wrapper img");
  const closeOverlay = select(".overlay__close");
  const count = select(".count");
  const countOf = select(".of");
  const bottomLeft = select(".bottom__left");
  const imgWrapper = select(".overlay__img__wrapper");
  const contentImages = selectAll(".element__content__image img");

  if (!imgWrapper) return;

  // --- Build slides from element__content__inner sections ---
  const details = gsap.utils.toArray(".element__content__inner");
  const slides = []; // { img: HTMLImageElement, caption: string }

  details.forEach((detail) => {
    const detailImgs = detail.querySelectorAll(".element__content__image img");
    const detailTexts = detail.querySelectorAll(
      ".element__content__text p:not(.english)",
    );
    const captionHtml = detailTexts.length
      ? Array.from(detailTexts)
          .map((p) => p.outerHTML)
          .join("")
      : "";

    detailImgs.forEach((srcImg) => {
      const newImg = document.createElement("img");
      newImg.className = "image";
      newImg.src = srcImg.getAttribute("src");
      newImg.alt = srcImg.getAttribute("alt") || "";
      imgWrapper.appendChild(newImg);
      slides.push({ img: newImg, caption: captionHtml });
    });
  });

  if (!slides.length) return;

  // Reverse to match original stacking order (last on top)
  slides.reverse();

  const images = slides.map((s) => s.img);
  const captions = slides.map((s) => s.caption);

  const wrap = gsap.utils.wrap(0, images.length);

  if (count) count.textContent = String(1);
  if (countOf) countOf.textContent = `0${images.length}`;

  const updateCaption = (index) => {
    if (!bottomLeft) return;
    bottomLeft.innerHTML = captions[index] || "";
  };

  let animating = false;
  let currentIndex = 0;

  function gotoSection(index, direction) {
    if (!count) return;
    animating = true;
    index = wrap(index);
    const tl = gsap.timeline({
      defaults: { duration: 1, ease: "expo.inOut" },
      onComplete: () => {
        animating = false;
      },
    });
    tl.set(count, { textContent: String(`0${index + 1}`) }, 0.32)
      .add(() => updateCaption(index), 0.32)
      .fromTo(
        images[index],
        { xPercent: 125 * direction, scaleX: 1.5, scaleY: 1.3 },
        { xPercent: 0, scaleX: 1, scaleY: 1, duration: 1 },
        0,
      )
      .fromTo(
        images[currentIndex],
        { xPercent: 0, scaleX: 1, scaleY: 1 },
        { xPercent: -125 * direction, scaleX: 1.5, scaleY: 1.3 },
        0,
      )
      .timeScale(0.8);
    currentIndex = index;
  }

  let observerInstance = null;
  Observer.create({
    id: "page-overlay",
    type: "wheel,touch,pointer",
    preventDefault: true,
    wheelSpeed: -1,
    onUp: () => {
      if (!animating) gotoSection(currentIndex + 1, +1);
    },
    onDown: () => {
      if (!animating) gotoSection(currentIndex - 1, -1);
    },
    tolerance: 10,
  });
  observerInstance = Observer.getById("page-overlay");
  if (observerInstance) observerInstance.disable();

  const onKeydown = (e) => {
    if (animating) return;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft")
      gotoSection(currentIndex - 1, -1);
    if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === "Enter")
      gotoSection(currentIndex + 1, 1);
  };

  // --- Funzione per aprire/chiudere la gallery ---
  const openGallery = () => {
    if (!elImage || !overlay) return;
    elImage.classList.add("active");
    overlay.classList.add("active");
    updateCaption(currentIndex);
    observerInstance?.enable();
    document.addEventListener("keydown", onKeydown);
  };

  const closeGallery = () => {
    if (!elImage || !overlay) return;
    elImage.classList.remove("active");
    overlay.classList.remove("active");
    observerInstance?.disable();
    document.removeEventListener("keydown", onKeydown);
  };

  if (elImage && overlay) {
    elImage.addEventListener("click", () => {
      if (overlay.classList.contains("active")) {
        closeGallery();
      } else {
        openGallery();
      }
    });
  }

  if (closeOverlay) {
    closeOverlay.addEventListener("click", () => {
      closeGallery();
    });
  }

  // --- Auto-open gallery all'arrivo sulla pagina prodotto ---
  const galleryDelay = 100;
  autoOpenGallery = false;
  openGallery();
  // setTimeout(() => {
  //   openGallery();
  // }, galleryDelay);

  if (contentImages?.length) {
    contentImages.forEach((img) => {
      gsap.set(img, { opacity: 0, y: 30, scale: 0.95 });
      const tl = gsap.timeline({
        paused: true,
        smoothChildTiming: true,
        onComplete: () => gsap.set(img, { clearProps: "opacity,transform" }),
        onReverseComplete: () =>
          gsap.set(img, { opacity: 0, y: 30, scale: 0.95 }),
      });
      tl.to(img, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.2,
        ease: "power2.out",
      });
      ScrollTrigger.create({
        animation: tl,
        trigger: img,
        start: "top 75%",
        end: "top 25%",
        scrub: true,
        fastScrollEnd: true,
      });
    });
  }
}

// --- LOGICA LOADER ---

const initLoader = () => {
  loaderInstance.start();
  loaderInstance.end();
  loaderInstance.animation.eventCallback("onComplete", () => {
    loaderInstance.hide();
    const introWrapper = select(".intro__wrapper");
    const enter = select(".intro__enter");
    const overlay = select(".overlay__loader");

    if (!introWrapper || !enter || !overlay) return;

    gsap.to(introWrapper, { duration: 1, display: "flex", opacity: 1 });

    const introImages = selectAll(".intro__images__wrapper .intro__img");

    // Pause all videos, start only the active one
    // function syncVideos(index) {
    //   introImages.forEach((el, i) => {
    //     if (el.tagName === "VIDEO") {
    //       if (i === index) {
    //         el.play();
    //       } else {
    //         el.pause();
    //         el.currentTime = 0;
    //       }
    //     }
    //   });
    // }

    if (introImages.length) {
      const wrap = gsap.utils.wrap(0, introImages.length);
      let animating = false;
      let currentIndex = 0;

      // Headline
      const introHeadline = select(".intro__headline");
      const introTexts = [
        "quality<br>beyond<br>the surface",
        "tarazum: onomatopoeia<br>representing the typical<br>sound of a fanfare or band.",
        "genuine<br>handcrafted<br>italian guitars",
      ];

      introImages.forEach((img, i) => {
        gsap.set(img, {
          xPercent: i === 0 ? 0 : 1,
          scaleX: i === 0 ? 1 : 5,
          scaleY: i === 0 ? 1 : 1.3,
          opacity: i === 0 ? 1 : 0,
          zIndex: introImages.length - i,
        });
      });

      if (introHeadline) introHeadline.innerHTML = introTexts[0];

      // eslint-disable-next-line no-inner-declarations
      function gotoSection(index, direction) {
        animating = true;
        index = wrap(index);
        const tl = gsap.timeline({
          defaults: { duration: 5, ease: "expo.inOut" },
          onComplete: () => {
            animating = false;
          },
        });
        tl.fromTo(
          introImages[index],
          { xPercent: 125 * direction, scaleX: 1.5, scaleY: 1.3, opacity: 0 },
          { xPercent: 0, scaleX: 1, scaleY: 1, opacity: 1, duration: 5 },
          0,
        )
          .fromTo(
            introImages[currentIndex],
            { xPercent: 0, scaleX: 1, scaleY: 1, opacity: 1 },
            {
              xPercent: -125 * direction,
              scaleX: 1.5,
              scaleY: 1.3,
              opacity: 0,
            },
            0,
          )
          // Fade out text
          .to(
            introHeadline,
            { duration: 0.4, opacity: 0, ease: "power2.in", delay: 0.4 },
            0,
          )
          // Text change
          .call(
            () => {
              introHeadline.innerHTML =
                introTexts[wrap(index) % introTexts.length];
            },
            [],
            4.2,
          )
          .to(
            introHeadline,
            { duration: 0.6, opacity: 1, ease: "power2.out" },
            4.2,
          )
          .timeScale(0.8);

        currentIndex = index;
      }

      Observer.create({
        id: "intro-overlay",
        type: "wheel,touch,pointer",
        preventDefault: true,
        wheelSpeed: -1,
        onUp: () => {
          if (!animating) gotoSection(currentIndex + 1, +1);
        },
        onDown: () => {
          if (!animating) gotoSection(currentIndex - 1, -1);
        },
        tolerance: 10,
      });

      // Autoplay
      gsap.delayedCall(3, function autoNext() {
        if (!animating) gotoSection(currentIndex + 1, +1);
        gsap.delayedCall(3, autoNext);
      });
    }

    enter.onclick = (e) => {
      Observer.getById("intro-overlay").disable();
      e.preventDefault();
      gsap.to(overlay, {
        duration: 0.3,
        opacity: 0,
        onComplete: () => {
          overlay.style.pointerEvents = "none";
          overlay.style.zIndex = -1;
        },
      });
    };
  });
};

// --- BARBA JS CONFIG ---

barba.init({
  sync: true,
  debug: true,

  views: [
    {
      namespace: "home",
      beforeEnter() {
        const loader = document.querySelector(".overlay__loader");
        if (loader && !isFirstLoad) {
          gsap.set(loader, { autoAlpha: 0, zIndex: -1 });
        }
      },
      afterEnter() {
        initHomeMap();
        initUIEvents();

        // --- LOGICA RITORNO MODALE ---
        // Se siamo tornati da un prodotto via modale, riapriamo la modale
        if (restoreModal && lastModalArgs) {
          // Usiamo un piccolo ritardo per assicurarci che il DOM e la Mappa siano pronti
          setTimeout(() => {
            // Riaprendo la modale, openModal.js dovrebbe reinizializzare Swiper
            openModalTracked(lastModalArgs.elName, lastModalArgs.options);
          }, 300); // Ritardo leggero per sync con animazione

          // Reset flag
          restoreModal = false;
        }
      },
      beforeLeave() {
        destroyMap();
      },
    },
    {
      namespace: "product",
      afterEnter() {
        initProductPage();
        initUIEvents();
      },
      beforeLeave() {
        ScrollTrigger.getAll().forEach((t) => t.kill());
        const obs = Observer.getById("page-overlay");
        if (obs) obs.kill();
      },
    },
  ],

  transitions: [
    // TRANSITION 1: Home -> Product (Click su .modal__image)
    // Effetto: La modale diventa bianca, si espande a tutto schermo da DX a SX, poi fade-in pagina
    {
      name: "home-to-product",
      from: { namespace: "home" },
      to: { namespace: "product" },

      custom: ({ trigger }) => {
        return trigger.classList && trigger.classList.contains("modal__image");
      },

      leave(data) {
        // Flag per riaprire la modale al ritorno
        restoreModal = true;
        // Flag per aprire automaticamente la gallery sulla pagina prodotto
        autoOpenGallery = true;
        const done = this.async();

        // Selezioniamo la modale e il suo contenuto
        const modalDialog =
          data.current.container.querySelector(".overlay__modal");
        const modalContent = modalDialog ? modalDialog.children : [];

        const tl = gsap.timeline({
          onComplete: done,
        });

        if (modalDialog) {
          // 1. Nascondiamo istantaneamente il contenuto della modale (testi, bottoni)
          // lasciando solo il box bianco
          tl.to(modalContent, {
            opacity: 0,
            duration: 0.2,
            ease: "power1.out",
          });

          // 2. Espandiamo il box bianco da destra a sinistra
          // Usiamo fromTo per forzare le coordinate di partenza corrette
          tl.fromTo(
            modalDialog,
            {
              maxWidth: "480px",
              width: "33%",
              backgroundColor: "#ffffff", // Assicuriamo che sia bianco
              zIndex: 9999,
            },
            {
              width: "100%", // Espansione totale verso sinistra
              maxWidth: "100%",
              duration: 0.8,
              ease: "expo.inOut",
              clearProps: "all",
            },
            "<", // Parte insieme al fade out del testo (o leggermente dopo con "-=0.1")
          );
        } else {
          // Fallback di sicurezza se non trova la modale
          tl.to(data.current.container, { opacity: 0, duration: 0.5 });
        }
      },

      enter(data) {
        // La nuova pagina entra in Fade In sopra il box bianco espanso
        return gsap.from(data.next.container, {
          opacity: 0,
          duration: 0.6,
          delay: 0.2, // Aspettiamo che l'espansione sia quasi finita
          ease: "power2.out",
          zIndex: 10000, // Sopra a tutto
          clearProps: "all",
        });
      },
    },

    // TRANSITION 2: Product -> Home (Click su .back)
    // Manteniamo lo slide out verso destra che funzionava bene ed è coerente
    {
      name: "back-to-home",
      from: { namespace: "product" },
      to: { namespace: "home" },

      custom: ({ trigger }) => {
        return trigger.classList && trigger.classList.contains("back");
      },

      leave(data) {
        // Il Prodotto scivola via verso destra scoprendo la home sotto
        return gsap.to(data.current.container, {
          x: "100%",
          opacity: 1, // Manteniamo opacità piena mentre esce
          duration: 0.8,
          ease: "expo.inOut",
          zIndex: 1000,
        });
      },

      enter(data) {
        // La Home appare sotto (statica o leggero fade)
        return gsap.from(data.next.container, {
          opacity: 0.5,
          duration: 0.8,
          ease: "power2.out",
          clearProps: "all",
        });
      },
    },

    // TRANSITION 3: Fallback generico
    {
      name: "default-fade",
      leave(data) {
        return gsap.to(data.current.container, { opacity: 0, duration: 0.5 });
      },
      enter(data) {
        return gsap.from(data.next.container, { opacity: 0, duration: 0.5 });
      },
    },
  ],
});

// Primo caricamento
window.addEventListener("DOMContentLoaded", () => {
  const container = document.querySelector('[data-barba="container"]');
  const ns = container.getAttribute("data-barba-namespace");

  if (ns === "home" && document.querySelector(".overlay__loader")) {
    initLoader();
    initHomeMap();
  } else if (ns === "product") {
    const l = document.querySelector(".overlay__loader");
    if (l) l.style.display = "none";
    initProductPage();
  }

  initUIEvents();
  isFirstLoad = false;
});
